import React, { useEffect, useRef, useState } from "react";
import {
  IConformationRegularBreedAwards,
  IConformationVarietyAwards,
  IEventSummary,
  IScore,
  IShow,
} from "@eagerdog/interfaces";
import {
  buildBreedPayload,
  buildClassSections,
  classSectionDogs,
  classSectionKey,
  licensedPools,
  varietyNames as coreVarietyNames,
} from "@eagerdog/scoring-core";
import { AxiosError } from "axios";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { AwardDropdown, PlacementGrid, SaveNote } from "./awardFields";
import { padPlacements, shortLevel } from "./dogLabel";
import { findClassPlacements, findStreamBreed, resolveAwards } from "./storedResults";

import styles from "./UKCConformationResults.module.scss";

interface IProps {
  stream: "regular" | "altered";
  breed: string;
  breedGroup: string;
  scores: IScore[];
  event: IEventSummary;
  show: IShow;
  onSaved: () => void;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

const sectionKey = classSectionKey;
const dogsFor = classSectionDogs;

const LicensedBreedCard: React.FC<IProps> = ({ stream, breed, breedGroup, scores, event, show, onSaved }) => {
  const sections = buildClassSections(scores);
  const varietyNames = coreVarietyNames(scores);

  const saved = findStreamBreed(show, breed, breedGroup, stream);
  const useLegacyWinners = stream === "regular" && !saved?.stream;

  const readPlacements = () => {
    const map: Record<string, string[]> = {};
    sections.forEach((section) => {
      map[sectionKey(section)] = findClassPlacements(
        saved?.class_results,
        section.level,
        section.sex,
        section.variety
      );
    });
    return map;
  };

  const readAwards = (): IConformationRegularBreedAwards => resolveAwards(saved, useLegacyWinners);

  const [placements, setPlacements] = useState<Record<string, string[]>>(readPlacements);
  const [awards, setAwards] = useState<IConformationRegularBreedAwards>(readAwards);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const formRef = useRef({ placements: readPlacements(), awards: readAwards() });
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (saveTimerRef.current) return;
    const nextPlacements = readPlacements();
    const nextAwards = readAwards();
    setPlacements(nextPlacements);
    setAwards(nextAwards);
    formRef.current = { placements: nextPlacements, awards: nextAwards };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const triggerAutoSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    setSaveStatus("idle");

    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      const current = formRef.current;

      try {
        await apiService.saveConformationBreedResults(
          event._id,
          show._id,
          buildBreedPayload({
            stream,
            breed,
            breedGroup,
            sections,
            placementsBySection: current.placements,
            awards: current.awards,
          })
        );
        setSaveStatus("saved");
        onSaved();
        savedStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        setSaveStatus("error");
        toast.show({
          title: "Error",
          content: "Failed to save breed results.",
          type: "fail",
          errorDetails: error as AxiosError,
        });
      } finally {
        saveTimerRef.current = null;
      }
    }, 800);
  };

  const updatePlacement = (key: string, index: number, id: string) => {
    setPlacements((prev) => {
      const next = { ...prev, [key]: [...(prev[key] || padPlacements())] };
      next[key][index] = id;
      formRef.current.placements = next;
      return next;
    });
    triggerAutoSave();
  };

  const updateAward = (patch: Partial<IConformationRegularBreedAwards>) => {
    setAwards((prev) => {
      const next = { ...prev, ...patch };
      formRef.current.awards = next;
      return next;
    });
    triggerAutoSave();
  };

  const updateVariety = (variety: string, patch: Partial<IConformationVarietyAwards>) => {
    setAwards((prev) => {
      const rows = [...(prev.variety_awards || [])];
      const index = rows.findIndex((row) => row.variety === variety);
      const current = index >= 0 ? rows[index] : { variety };
      const nextRow = { ...current, ...patch, variety };
      if (index >= 0) rows[index] = nextRow;
      else rows.push(nextRow);
      const next = { ...prev, variety_awards: rows };
      formRef.current.awards = next;
      return next;
    });
    triggerAutoSave();
  };

  const pools = licensedPools(scores, placements, awards);
  const { males, females, champions, grandChampions } = pools;

  const varietyRow = (name: string): IConformationVarietyAwards =>
    awards.variety_awards?.find((row) => row.variety === name) || { variety: name };

  return (
    <div className={styles.breedCard}>
      <div className={styles.breedCardHeader}>
        <span className={styles.breedName}>{breed}</span>
        <span className={styles.breedEntryCount}>
          {scores.length} entr{scores.length === 1 ? "y" : "ies"}
        </span>
      </div>

      {sections.map((section) => {
        const key = sectionKey(section);
        const title = [
          shortLevel(section.level),
          section.sex === "Male" ? "Male" : "Female",
          section.variety,
        ].filter(Boolean).join(" — ");
        return (
          <div className={styles.classSection} key={key}>
            <div className={styles.classSectionTitle}>{title}</div>
            <PlacementGrid
              scores={dogsFor(scores, section)}
              value={placements[key] || padPlacements()}
              onChange={(index, id) => updatePlacement(key, index, id)}
            />
          </div>
        );
      })}

      <div className={styles.sectionDivider}>Breed awards</div>
      <div className={styles.awardGrid}>
        {males.length > 0 && (
          <>
            <AwardDropdown
              label="Best Male (BM)"
              scores={pools.bestMale}
              value={awards.best_male || ""}
              onChange={(id) => updateAward({ best_male: id })}
            />
            <AwardDropdown
              label="Reserve Best Male (RBM)"
              scores={pools.bestMale}
              value={awards.reserve_best_male || ""}
              onChange={(id) => updateAward({ reserve_best_male: id })}
            />
          </>
        )}
        {females.length > 0 && (
          <>
            <AwardDropdown
              label="Best Female (BF)"
              scores={pools.bestFemale}
              value={awards.best_female || ""}
              onChange={(id) => updateAward({ best_female: id })}
            />
            <AwardDropdown
              label="Reserve Best Female (RBF)"
              scores={pools.bestFemale}
              value={awards.reserve_best_female || ""}
              onChange={(id) => updateAward({ reserve_best_female: id })}
            />
          </>
        )}
        {pools.classDogs.length > 0 && (
          <AwardDropdown
            label="Best of Winners (BOW)"
            scores={pools.bestOfWinners}
            value={awards.best_of_winners || ""}
            onChange={(id) => updateAward({ best_of_winners: id })}
          />
        )}
        {champions.length > 0 && (
          <>
            <AwardDropdown
              label="Champion"
              scores={champions}
              value={awards.champion || ""}
              onChange={(id) => updateAward({ champion: id })}
            />
            <AwardDropdown
              label="Reserve Champion"
              scores={champions}
              value={awards.reserve_champion || ""}
              onChange={(id) => updateAward({ reserve_champion: id })}
            />
          </>
        )}
        {grandChampions.length > 0 && (
          <>
            <AwardDropdown
              label="Grand Champion"
              scores={grandChampions}
              value={awards.grand_champion || ""}
              onChange={(id) => updateAward({ grand_champion: id })}
            />
            <AwardDropdown
              label="Reserve Grand Champion"
              scores={grandChampions}
              value={awards.reserve_grand_champion || ""}
              onChange={(id) => updateAward({ reserve_grand_champion: id })}
            />
          </>
        )}
        <AwardDropdown
          label="Best of Breed (BOB)"
          scores={pools.bestOfBreed}
          value={awards.best_of_breed || ""}
          onChange={(id) => updateAward({ best_of_breed: id })}
        />
      </div>

      {varietyNames.map((variety) => {
        const row = varietyRow(variety);
        const varietyPool = pools.varieties.find((item) => item.variety === variety);
        const varietyMales = varietyPool?.males || [];
        const varietyFemales = varietyPool?.females || [];
        return (
          <div key={variety}>
            <div className={styles.sectionDivider}>{variety}</div>
            <div className={styles.awardGrid}>
              {varietyMales.length > 0 && (
                <>
                  <AwardDropdown
                    label="Best Male of Variety"
                    scores={varietyMales}
                    value={row.best_male || ""}
                    onChange={(id) => updateVariety(variety, { best_male: id })}
                  />
                  <AwardDropdown
                    label="Reserve Best Male of Variety"
                    scores={varietyMales}
                    value={row.reserve_best_male || ""}
                    onChange={(id) => updateVariety(variety, { reserve_best_male: id })}
                  />
                </>
              )}
              {varietyFemales.length > 0 && (
                <>
                  <AwardDropdown
                    label="Best Female of Variety"
                    scores={varietyFemales}
                    value={row.best_female || ""}
                    onChange={(id) => updateVariety(variety, { best_female: id })}
                  />
                  <AwardDropdown
                    label="Reserve Best Female of Variety"
                    scores={varietyFemales}
                    value={row.reserve_best_female || ""}
                    onChange={(id) => updateVariety(variety, { reserve_best_female: id })}
                  />
                </>
              )}
            </div>
          </div>
        );
      })}

      <SaveNote status={saveStatus} />
    </div>
  );
};

export default LicensedBreedCard;
